import React, { useCallback, useMemo } from 'react';

import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { DimensionInput } from 'ui-components/components/dimension-input';

import { UnitPosition, UnitPositionPropType } from '../../enums/units/unit-position';
import { convertDimensions } from '../../services/unit-services/dimensions/dimension-convert-service';
import Global from '../../store/global-selectors';
import { useFulfillmentPlannerContext } from '../context';

const checkDidDimensionsChange = (dimensions, newDimensions) => {
  const keys = Object.keys(dimensions);
  return keys.some((key) => dimensions[key] !== newDimensions[key]);
};

const FulfillmentPlannerDimensions = ({
  className = null,
  inputClassName = null,
  shrinkable = false,
  unitPosition = UnitPosition.OutsideEnd,
  numberInputsContainerClassName,
  labelId,
  fulfillmentPlanId = null,
}) => {
  const sellerDefaultDimensionUnit = useSelector(Global.selectSellerDefaultDimensionUnit);
  const { onWeightAndDimensionsChange, packages, setHasSmartFillDataBeenChanged } = useFulfillmentPlannerContext();
  const [firstPackage = {}] = packages;
  const dimensions = useMemo(() => {
    return convertDimensions(firstPackage.dimensions, sellerDefaultDimensionUnit) || {};
  }, [firstPackage.dimensions, sellerDefaultDimensionUnit]);

  const inputDimensions = useMemo(() => {
    return {
      length: dimensions.length,
      width: dimensions.width,
      height: dimensions.height,
    };
  }, [dimensions]);

  const onChange = useCallback(
    (newDimensions, dimension) => {
      if (checkDidDimensionsChange(dimensions, newDimensions)) {
        onWeightAndDimensionsChange(undefined, newDimensions);
        setHasSmartFillDataBeenChanged((prevState) => ({
          ...prevState,
          [dimension]: true,
        }));
      }
    },
    [dimensions, onWeightAndDimensionsChange, setHasSmartFillDataBeenChanged],
  );

  return (
    <DimensionInput
      className={className}
      dimensions={inputDimensions}
      disabled={packages.length > 1}
      fulfillmentPlanId={fulfillmentPlanId}
      inputClassName={inputClassName}
      labelId={labelId}
      numberInputsContainerClassName={numberInputsContainerClassName}
      onChange={onChange}
      shouldBlockUpdateIfFocused
      shrinkable={shrinkable}
      unit={firstPackage?.dimensions?.unit ?? sellerDefaultDimensionUnit}
      unitPosition={unitPosition}
    />
  );
};

export default FulfillmentPlannerDimensions;

FulfillmentPlannerDimensions.propTypes = {
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  shrinkable: PropTypes.bool,
  unitPosition: UnitPositionPropType,
  numberInputsContainerClassName: PropTypes.string,
  labelId: PropTypes.string,
};
